import { Box, Breadcrumbs, Container, Link, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import logo from '../assets/images/assessments-blue.svg';
import LoadingIndicator from '../components/LoadingIndicator';
import AssessmentDone from '../components/assessment/AssessmentDone';
import AssessmentEvaluation from '../components/assessment/AssessmentEvaluation';
import AssessmentPhases from '../components/assessment/AssessmentPhases';
import AssessmentPlan from '../components/assessment/AssessmentPlan';
import AssessmentReview from '../components/assessment/AssessmentReview';
import AssessmentSelection from '../components/assessment/AssessmentSelection';
import { LoadStatus } from '../constants/constants';
import {
  AssessmentSteps,
  selectAssessmentLoadStatus,
  selectCompletion,
  selectCurrentStep,
  selectSelectedAssessment,
  setAssessmentStep,
  updateCompletion,
} from '../redux/reducers/assessmentReducer';
import { selectSelectedOrganization } from '../redux/reducers/organizationReducer';
import FilterBar from '../components/assessment/FilterBar';

function AssessmentView() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedOrganization = useSelector(selectSelectedOrganization);
  const selectedAssessment = useSelector(selectSelectedAssessment);
  const assessmentLoadStatus = useSelector(selectAssessmentLoadStatus);
  const completion = useSelector(selectCompletion);
  const assessmentStep = useSelector(selectCurrentStep);

  useEffect(() => {
    if (completion && completion !== 'NaN') {
      dispatch(updateCompletion(completion));
    }
  }, [completion]);

  const handleStepChange = (step) => {
    dispatch(setAssessmentStep(step));
  };

  const renderTitle = (step) => {
    if (step !== AssessmentSteps.ASSESSMENT_SELECTION && assessmentLoadStatus !== LoadStatus.SUCCESS) {
      return false;
    }
    switch (step) {
      case AssessmentSteps.DONE:
      case AssessmentSteps.REVIEW:
      case AssessmentSteps.EVALUATION:
      case AssessmentSteps.PLAN:
        return (
          <Box sx={{ maxWidth: '900px' }}>
            {assessmentLoadStatus !== LoadStatus.SUCCESS ? (
              <LoadingIndicator />
            ) : (
              <Stack spacing={1}>
                <Stack direction="row" alignContent="center" spacing={1}>
                  <img src={logo} width="32px" alt="" />
                  <Breadcrumbs aria-label="breadcrumb">
                    <Typography variant="overline">{selectedOrganization.name.toUpperCase()}</Typography>
                    <Link
                      variant="overline"
                      underline="hover"
                      component="button"
                      onClick={() => handleStepChange(AssessmentSteps.ASSESSMENT_SELECTION)}
                    >
                      {t('assessment.assessments').toUpperCase()}
                    </Link>
                    {step === AssessmentSteps.DONE ? (
                      <Typography variant="overline">{selectedAssessment.name.toUpperCase()}</Typography>
                    ) : (
                      <Link
                        variant="overline"
                        underline="hover"
                        component="button"
                        onClick={() => handleStepChange(AssessmentSteps.PLAN)}
                      >
                        {selectedAssessment.name || ''}
                      </Link>
                    )}
                  </Breadcrumbs>
                </Stack>
                <Stack>
                  <Typography>{selectedAssessment.description}</Typography>
                </Stack>
              </Stack>
            )}
          </Box>
        );
      case AssessmentSteps.ASSESSMENT_SELECTION: // deliberate fallthrough
      default:
        return (
          <Stack spacing={3} sx={{ maxWidth: '900px' }}>
            <Typography>{t('assessment.assessmentHeaderModule')}</Typography>
            <Typography>{t('assessment.assessmentHeaderUsing')}</Typography>
          </Stack>
        );
    }
  };

  const renderContent = (step) => {
    if (step !== AssessmentSteps.ASSESSMENT_SELECTION && assessmentLoadStatus !== LoadStatus.SUCCESS) {
      return <LoadingIndicator />;
    }
    switch (step) {
      case AssessmentSteps.DONE:
        return <AssessmentDone />;
      case AssessmentSteps.REVIEW:
        return <AssessmentReview />;
      case AssessmentSteps.EVALUATION:
        return <AssessmentEvaluation />;
      case AssessmentSteps.PLAN:
        return <AssessmentPlan />;
      case AssessmentSteps.ASSESSMENT_SELECTION: // deliberate fallthrough
      default:
        return <AssessmentSelection handleStepChange={handleStepChange} />;
    }
  };

  const renderPhases = (step) => {
    if (step !== AssessmentSteps.ASSESSMENT_SELECTION && assessmentLoadStatus !== LoadStatus.SUCCESS) {
      return false;
    }
    switch (step) {
      case AssessmentSteps.REVIEW: // deliberate fallthrough
      case AssessmentSteps.PLAN:
        return <AssessmentPhases handleStepChange={handleStepChange} />;
      case AssessmentSteps.EVALUATION:
        return (
          <Stack>
            <AssessmentPhases handleStepChange={handleStepChange} />
            <Stack spacing={3}>
              {/* Let's hide this for now to save vertical space - Olli */}
              {/* <Typography variant="body1">{t('control.evaluation.title')}</Typography> */}
              <FilterBar />
            </Stack>
          </Stack>
        );
      case AssessmentSteps.DONE: // deliberate fallthrough
      default:
        return false;
    }
  };

  return (
    <Container variant="baseView" maxWidth={false} disableGutters>
      {assessmentStep === AssessmentSteps.ASSESSMENT_SELECTION && (
        <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: '24px' }}>
          <img src={logo} />
          <Typography id='assessments-title' variant="title">{t('assessment.assessments')}</Typography>
        </Stack>
      )}
      <Box>
        {renderTitle(assessmentStep)}
        {renderPhases(assessmentStep)}
      </Box>

      <Container
        variant={assessmentStep === AssessmentSteps.EVALUATION ? 'evaluationContentArea' : 'assessmentDataView'}
        maxWidth={false}
        disableGutters
      >
        {renderContent(assessmentStep)}
      </Container>
    </Container>
  );
}

export default AssessmentView;
